import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

interface PiramideRow {
  faixa_etaria: string;
  sexo: string;
  total: number;
}

interface BairroRow {
  bairro: string;
  total: number;
}

interface RacaRow {
  raca: string;
  total: number;
}

interface PacientesData {
  kpis: {
    total_pacientes: number;
    feminino: number;
    masculino: number;
    idade_media: number;
    com_cpf: number;
  };
  piramide: PiramideRow[];
  topBairros: BairroRow[];
  porRaca: RacaRow[];
}

const RACA_COLORS = [
  '#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#c62828',
  '#00838f', '#4527a0', '#ad1457', '#ef6c00', '#795548',
];

const MonitoramentoPacientes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<PacientesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getPacientes(municipioAtivo.id);
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de pacientes.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo]);

  // Separate piramide data by sex
  const piramideMasculino = useMemo(() => {
    if (!data?.piramide) return [];
    return data.piramide
      .filter((r) => r.sexo === 'M' || r.sexo === 'MASCULINO')
      .map((r) => ({ faixa_etaria: r.faixa_etaria, total: r.total }))
      .sort((a, b) => {
        // Sort by age group - try to extract numeric start for ordering
        const numA = parseInt(a.faixa_etaria, 10);
        const numB = parseInt(b.faixa_etaria, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.faixa_etaria.localeCompare(b.faixa_etaria);
      });
  }, [data?.piramide]);

  const piramideFeminino = useMemo(() => {
    if (!data?.piramide) return [];
    return data.piramide
      .filter((r) => r.sexo === 'F' || r.sexo === 'FEMININO')
      .map((r) => ({ faixa_etaria: r.faixa_etaria, total: r.total }))
      .sort((a, b) => {
        const numA = parseInt(a.faixa_etaria, 10);
        const numB = parseInt(b.faixa_etaria, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.faixa_etaria.localeCompare(b.faixa_etaria);
      });
  }, [data?.piramide]);

  // Max value for piramide x-axis alignment
  const piramideMaxValue = useMemo(() => {
    const allTotals = [...piramideMasculino, ...piramideFeminino].map((r) => r.total);
    return allTotals.length > 0 ? Math.max(...allTotals) : 0;
  }, [piramideMasculino, piramideFeminino]);

  // Custom tooltip for pie chart
  const renderPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      return (
        <Paper sx={{ px: 1.5, py: 1, borderRadius: 1 }} elevation={3}>
          <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {item.value.toLocaleString('pt-BR')} ({((item.value / (data?.kpis?.total_pacientes || 1)) * 100).toFixed(1)}%)
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Selecione um municipio para visualizar os dados de pacientes.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis;
  const topBairros = data?.topBairros || [];
  const porRaca = data?.porRaca || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Pacientes
      </Typography>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md>
          <KpiCard
            titulo="Total Pacientes"
            valor={(kpis?.total_pacientes ?? 0).toLocaleString('pt-BR')}
            cor="#1565c0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <KpiCard
            titulo="Feminino"
            valor={(kpis?.feminino ?? 0).toLocaleString('pt-BR')}
            cor="#e91e63"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <KpiCard
            titulo="Masculino"
            valor={(kpis?.masculino ?? 0).toLocaleString('pt-BR')}
            cor="#1565c0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <KpiCard
            titulo="Idade Media"
            valor={(kpis?.idade_media ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
            cor="#6a1b9a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <KpiCard
            titulo="Com CPF"
            valor={(kpis?.com_cpf ?? 0).toLocaleString('pt-BR')}
            cor="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* Piramide Etaria */}
      {(piramideMasculino.length > 0 || piramideFeminino.length > 0) && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Piramide Etaria
          </Typography>
          <Grid container spacing={0}>
            {/* Masculino (left) - reversed X axis */}
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#1565c0">
                  Masculino
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={Math.max(300, piramideMasculino.length * 32)}>
                <BarChart
                  data={piramideMasculino}
                  layout="vertical"
                  margin={{ left: 10, right: 0, top: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    reversed
                    domain={[0, piramideMaxValue]}
                  />
                  <YAxis
                    type="category"
                    dataKey="faixa_etaria"
                    tick={{ fontSize: 11 }}
                    orientation="right"
                    width={70}
                  />
                  <Tooltip
                    formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                  />
                  <Bar dataKey="total" name="Masculino" fill="#1565c0" radius={[4, 0, 0, 4]} />
                </BarChart>
              </ResponsiveContainer>
            </Grid>

            {/* Feminino (right) */}
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#e91e63">
                  Feminino
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={Math.max(300, piramideFeminino.length * 32)}>
                <BarChart
                  data={piramideFeminino}
                  layout="vertical"
                  margin={{ left: 0, right: 10, top: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    domain={[0, piramideMaxValue]}
                  />
                  <YAxis
                    type="category"
                    dataKey="faixa_etaria"
                    hide
                  />
                  <Tooltip
                    formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                  />
                  <Bar dataKey="total" name="Feminino" fill="#e91e63" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Top Bairros */}
        {topBairros.length > 0 && (
          <Grid item xs={12} md={7}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Top Bairros
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topBairros}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="bairro"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                  />
                  <Bar dataKey="total" name="Pacientes" fill="#1565c0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Por Raca - PieChart */}
        {porRaca.length > 0 && (
          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Distribuicao por Raca/Cor
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={porRaca}
                    dataKey="total"
                    nameKey="raca"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    innerRadius={50}
                    paddingAngle={2}
                    label={({ payload, percent }: any) =>
                      `${payload?.raca || ''} (${((percent || 0) * 100).toFixed(0)}%)`
                    }
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {porRaca.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={RACA_COLORS[index % RACA_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={renderPieTooltip} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Empty state */}
      {!kpis && piramideMasculino.length === 0 && piramideFeminino.length === 0 && topBairros.length === 0 && porRaca.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum dado disponivel para pacientes.
          </Typography>
        </Box>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="pacientes"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoPacientes;
